import * as THREE from 'three';
import { Entity } from './Entity.js';

/**
 * Fusion particle effect entity.
 *
 * Spawns when parts are successfully fused, creating a spectacular visual effect.
 * Particles spiral outward in a radial pattern with cyan/white colors.
 *
 * Visual:
 * - Cyan/white colored particles
 * - Radial/spiral emission pattern
 * - More particles than impact effect (20)
 * - Longer lifetime (0.5 seconds)
 * - No gravity (floating effect)
 *
 * Behavior:
 * - Lifetime of 0.5 seconds
 * - 20 particles emitted
 * - Spiral motion pattern
 * - Automatically destroyed when lifetime expires
 */
export class FusionEffect extends Entity {
  private particles: THREE.Points;
  private lifetime: number = 0.5;
  private velocities: THREE.Vector3[] = [];
  private rotationSpeeds: number[] = [];

  /**
   * Create a new fusion effect.
   *
   * @param x - X position in world space
   * @param y - Y position in world space (will be at y=0.5 height)
   */
  constructor(x: number, y: number) {
    super(x, y, 0);
    this.createParticles();
  }

  /**
   * Create the particle system with spiral pattern.
   */
  private createParticles(): void {
    const geometry = new THREE.BufferGeometry();
    const count = 20;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);

    // Initialize all particles at the fusion point (height 0.5)
    for (let i = 0; i < count; i++) {
      positions[i * 3] = 0;
      positions[i * 3 + 1] = 0.5;
      positions[i * 3 + 2] = 0;

      // Generate radial velocity with spiral component
      const angle = (i / count) * Math.PI * 2;
      const speed = 3 + Math.random() * 2;
      const spiralSpeed = Math.random() * 2;

      this.velocities.push(
        new THREE.Vector3(
          Math.cos(angle) * speed,
          (Math.random() - 0.5) * spiralSpeed, // Slight vertical variation
          Math.sin(angle) * speed
        )
      );

      // Store rotation speed for spiral effect
      this.rotationSpeeds.push((Math.random() - 0.5) * 4);

      // Alternate between cyan and white colors
      const color = new THREE.Color(i % 2 === 0 ? 0x00ffff : 0xffffff);
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.2,
      transparent: true,
      vertexColors: true,
      blending: THREE.AdditiveBlending, // Glowy effect
    });

    this.particles = new THREE.Points(geometry, material);
    this.mesh = this.particles;

    // Update mesh to match entity position
    this.updateMeshTransform();
  }

  /**
   * Update particle positions and lifetime.
   *
   * @param dt - Fixed delta time in milliseconds
   */
  update(dt: number): void {
    const dtSec = dt / 1000;
    this.lifetime -= dtSec;

    // Destroy when lifetime expires
    if (this.lifetime <= 0) {
      this.destroy();
      return;
    }

    // Update particle positions with spiral motion
    const positions = this.particles.geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < this.velocities.length; i++) {
      // Apply rotation to create spiral
      const rotSpeed = this.rotationSpeeds[i];
      const vel = this.velocities[i];

      // Simple rotation around Y axis
      const cos = Math.cos(rotSpeed * dtSec);
      const sin = Math.sin(rotSpeed * dtSec);
      const newX = vel.x * cos - vel.z * sin;
      const newZ = vel.x * sin + vel.z * cos;
      vel.x = newX;
      vel.z = newZ;

      // Update position
      positions[i * 3] += vel.x * dtSec;
      positions[i * 3 + 1] += vel.y * dtSec;
      positions[i * 3 + 2] += vel.z * dtSec;
    }
    this.particles.geometry.attributes.position.needsUpdate = true;

    // Fade out based on remaining lifetime
    const material = this.particles.material as THREE.PointsMaterial;
    material.opacity = this.lifetime / 0.5;
  }
}
